// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  FileDownloadOutlined,
  UnfoldLess,
  UnfoldMore,
} from '@/constants/hardfin-standard-icons';
import { useDataGridRowExpansion } from '@/hooks/use-data-grid-row-expansion';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarContainerProps,
  gridFilteredSortedRowIdsSelector,
  gridRowTreeSelector,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import { omit } from 'lodash-es';
import { useRef } from 'react';

interface Props extends GridToolbarContainerProps {
  /**
   * A list of column names in the order they should appear in the exported
   * file. Any columns not included in this list will be exported in the order
   * they appear in the data.
   */
  columnOrder: string[];

  /**
   * A callback fired when the user clicks the export button that returns the
   * name of the file to be exported. This file name should not include a file
   * extension.
   */
  getFileName: () => string;
}

export function AssetReportToolbar(props: Props) {
  const theme = useTheme();
  const apiRef = useGridApiContext();
  const menu = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rowExpansion = useDataGridRowExpansion();

  const toolbarProps = omit(props, 'getFileName', 'columnOrder');
  const tooltipTitle = rowExpansion.isExpanded ? 'Collapse all' : 'Expand all';

  const compareFields = (field: string, other: string) => {
    const fieldIndex = props.columnOrder.indexOf(field);
    const otherIndex = props.columnOrder.indexOf(other);

    if (fieldIndex === otherIndex) {
      return 0;
    }

    if (fieldIndex === -1) {
      return 1;
    }

    if (otherIndex === -1) {
      return -1;
    }

    return fieldIndex < otherIndex ? -1 : 1;
  };

  const getRows = () => {
    const tree = gridRowTreeSelector(apiRef);
    const rows = gridFilteredSortedRowIdsSelector(apiRef);

    return rows.filter((rowId) => tree[rowId].type !== 'group');
  };

  const getFields = () => {
    const columnDefinitions = apiRef.current.getAllColumns();
    const fields = columnDefinitions.reduce<string[]>(
      (fields, columnDefinition) => {
        if (columnDefinition.disableExport) {
          return fields;
        }

        return [...fields, columnDefinition.field];
      },
      []
    );

    return fields.sort(compareFields);
  };

  const handleClickCSVExport = () => {
    const fields = getFields();
    const fileName = props.getFileName();

    apiRef.current.exportDataAsCsv({
      fields,
      fileName,
      getRowsToExport: getRows,
    });
  };

  const handleClickExcelExport = () => {
    const fields = getFields();
    const fileName = props.getFileName();

    apiRef.current.exportDataAsExcel({
      fields,
      fileName,
      getRowsToExport: getRows,
    });
  };

  const handleClickToggle = () => {
    if (rowExpansion.isExpanded) {
      return rowExpansion.collapse();
    }

    rowExpansion.expand();
  };

  return (
    <GridToolbarContainer
      {...toolbarProps}
      sx={{
        paddingBottom: 0.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Button
        ref={buttonRef}
        onClick={menu.open}
        startIcon={<FileDownloadOutlined />}
      >
        Export
      </Button>
      <Menu
        open={menu.isOpen}
        onClose={menu.close}
        anchorEl={buttonRef.current}
      >
        <MenuItem onClick={handleClickCSVExport}>Download as CSV</MenuItem>
        <MenuItem onClick={handleClickExcelExport}>Download as Excel</MenuItem>
      </Menu>
      <Tooltip arrow placement="top" title={tooltipTitle}>
        <IconButton
          size="small"
          onClick={handleClickToggle}
          sx={{ marginLeft: 'auto' }}
        >
          {rowExpansion.isExpanded ? <UnfoldLess /> : <UnfoldMore />}
        </IconButton>
      </Tooltip>
    </GridToolbarContainer>
  );
}
